import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn, signOut, getCurrentUser, fetchAuthSession, confirmSignIn } from 'aws-amplify/auth';
import { config } from '../config';

// BUILD-039 Owner visual review — LOCAL, DEV-ONLY auth bypass. Hard-guarded by
// import.meta.env.DEV (statically false and dead-code-eliminated in any `vite build`, matching
// the SAME guard discipline PM's devLocalAuth.ts already uses) AND an explicit opt-in env var, so
// this can NEVER activate in a shipped bundle and NEVER silently activates during ordinary local
// development against the real Cognito pool. It exists ONLY so Chris can visually review the
// redesigned Users & Access screen with realistic seeded people, without real Cognito.
const LOCAL_MOCK_AUTH = import.meta.env.DEV && import.meta.env.VITE_LOCAL_MOCK_AUTH === 'true';

if (!LOCAL_MOCK_AUTH) {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
      },
    },
  });
}

interface User {
  userId: string;
  email: string;
  name: string;
  groups: string[];
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  needsNewPassword: boolean;
  login: (email: string, password: string) => Promise<string>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// BUILD-039 Owner visual review — the fixed local mock identity. Owner group so the "Users &
// Access" tab is reachable. Not a real credential: getToken() below returns a fixed, obviously
// fake string, never a signed JWT, and it is only ever honored by the local mock API server this
// review setup also starts (which accepts any bearer value and runs on loopback only).
const MOCK_USER: User = {
  userId: 'local-mock-owner',
  email: 'local.reviewer@uniquecompanies.com',
  name: 'Local Reviewer',
  groups: ['owner'],
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(LOCAL_MOCK_AUTH ? MOCK_USER : null);
  const [isLoading, setIsLoading] = useState(!LOCAL_MOCK_AUTH);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);

  useEffect(() => {
    if (LOCAL_MOCK_AUTH) return; // already signed in as MOCK_USER; no real Cognito check needed.
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      setUser({
        userId: currentUser.userId,
        email: idToken?.payload?.email as string || '',
        name: `${idToken?.payload?.given_name || ''} ${idToken?.payload?.family_name || ''}`.trim(),
        groups: (idToken?.payload?.['cognito:groups'] as string[]) || [],
      });
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function login(email: string, password: string): Promise<string> {
    if (LOCAL_MOCK_AUTH) { setUser(MOCK_USER); return 'SIGNED_IN'; }
    const result = await signIn({ username: email, password });
    
    if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      setNeedsNewPassword(true);
      return 'NEW_PASSWORD_REQUIRED';
    }
    
    if (result.isSignedIn) {
      await checkAuth();
      return 'SIGNED_IN';
    }
    
    return result.nextStep?.signInStep || 'UNKNOWN';
  }

  async function completeNewPassword(newPassword: string): Promise<void> {
    const result = await confirmSignIn({ challengeResponse: newPassword });
    if (result.isSignedIn) {
      setNeedsNewPassword(false);
      await checkAuth();
    }
  }

  async function logout() {
    if (LOCAL_MOCK_AUTH) { setUser(null); return; }
    await signOut();
    setUser(null);
  }

  async function getToken(): Promise<string> {
    if (LOCAL_MOCK_AUTH) {
      // LOCAL PREVIEW ONLY (statically false in every deployed build). Still not a real token (unsigned, local claims), but shaped like one so the HQ permission screens can scope
      // their durable "unresolved change" record to an actor; a walkthrough can pretend to be another actor with localStorage 'local-mock-sub'.
      const b64 = (o: object) => btoa(JSON.stringify(o)).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
      let sub = 'local-mock-user'; try { sub = localStorage.getItem('local-mock-sub') || sub; } catch { /* keep default */ }
      return `${b64({ alg: 'none', typ: 'JWT' })}.${b64({ iss: 'local-mock-issuer', sub })}.local-mock-not-a-real-signature`;
    }
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, needsNewPassword, login, completeNewPassword, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
